import { useEffect, useMemo } from 'react';
import { EMPTY_INBOX_FILTROS, conversationFiltrosFrom } from '@/features/inbox/inbox-format';
import { useConversations } from '@/features/inbox/use-conversations';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { selectUser, useAuthStore } from '@/stores/auth-store';
import { useInboxAlertsStore } from '@/stores/inbox-alerts-store';
import {
  ALERTAS_VACIAS,
  calcularAlertas,
  entradasDeVistos,
  ultimoMensajeEn,
  type InboxAlertas,
} from './alerts-format';

/**
 * Avisos de mensajes nuevos del inbox: el dato que alimenta las burbujas del header y del menú
 * lateral, el aviso DENTRO de la bandeja y el punto azul de la fila.
 *
 * **Dónde vive**: en el SHELL (`AppLayout`), no en la pantalla Conversaciones, porque las burbujas
 * tienen que verse desde cualquier pantalla (es justo el problema que resuelve: el asesor está en
 * Productos y no se entera de que un cliente escribió). El hook se puede montar dos veces —shell y
 * bandeja— sin coste de red: las dos instancias usan los MISMOS filtros que la bandeja sin tocar, así
 * que TanStack Query comparte una sola consulta (una sola petición cada `INBOX_REFRESH_MS`) y el «ya
 * visto» está en `stores/inbox-alerts-store.ts`, compartido.
 *
 * **NO hay aviso flotante, a propósito**: los mensajes llegan todo el rato y una tarjeta que se abre
 * sola sobre la pantalla de trabajo es molesta (decisión del 25-sep-2026). Lo que sí hay es el aviso
 * **dentro de la bandeja** (`ConversationList`: cuántas conversaciones tienen mensajes sin ver + botón
 * «Marcar como visto») y el punto azul en cada fila.
 *
 * **Qué se lee**: la primera página de la bandeja por actividad (`limite`/`offset` por defecto), que
 * es donde cae cualquier mensaje nuevo. No hay endpoint de «no leídos»: ver la cabecera de
 * `alerts-format.ts` para el criterio y sus límites.
 *
 * **Los errores no se pintan aquí**: si la lectura falla (o no hay negocio activo), los avisos son
 * `ALERTAS_VACIAS` y las burbujas simplemente no aparecen. El error de la bandeja lo sigue contando la
 * pantalla Conversaciones, que es donde el usuario puede hacer algo al respecto.
 */

/**
 * Filtros de la consulta de avisos: la bandeja SIN filtrar, primera página (30 por actividad). Es
 * exactamente la consulta de `InboxPage` cuando el usuario no ha tocado los filtros: mismos valores
 * → misma query key → misma petición. Fuera de React y a nivel de módulo para no regenerar el objeto
 * en cada render.
 */
const ALERTAS_FILTROS = conversationFiltrosFrom(EMPTY_INBOX_FILTROS, 0);

export interface UseInboxAlertsOpciones {
  /** Conversación abierta en la bandeja: se marca vista sola mientras se lee. */
  conversacionAbierta?: string | null;
}

export interface InboxAlertasHook {
  /** Lo que pintan las burbujas y el aviso de la bandeja. */
  alertas: InboxAlertas;
  /** Da por vista toda la bandeja listada, sin abrir nada (botón del aviso de la bandeja). */
  marcarTodasVistas: () => void;
}

export function useInboxAlerts(opciones: UseInboxAlertsOpciones = {}): InboxAlertasHook {
  const { tenantId } = useActiveTenantAccess();
  const userId = useAuthStore(selectUser)?.id ?? null;

  // Ámbito de lo «visto»: negocio + usuario. Sin los dos no hay avisos (no se puede aislar el
  // estado) y las burbujas quedan apagadas.
  const ambito = tenantId && userId ? `${tenantId}:${userId}` : null;

  const conversacionesQuery = useConversations(tenantId, ALERTAS_FILTROS);
  const conversaciones = conversacionesQuery.data?.conversaciones ?? [];
  const cargado = conversacionesQuery.data !== undefined;

  const vistos = useInboxAlertsStore((state) => (ambito ? state.vistos[ambito] : undefined));
  const sembrado = useInboxAlertsStore((state) =>
    ambito ? state.sembrados[ambito] === true : false,
  );

  const sembrar = useInboxAlertsStore((state) => state.sembrar);
  const marcarVisto = useInboxAlertsStore((state) => state.marcarVisto);
  const marcarVarios = useInboxAlertsStore((state) => state.marcarVarios);

  const alertas = useMemo(
    () => (ambito ? calcularAlertas(conversaciones, vistos ?? {}, sembrado) : ALERTAS_VACIAS),
    [ambito, conversaciones, vistos, sembrado],
  );

  /**
   * Siembra: la bandeja que había la primera vez se da por vista. Se espera a tener datos (con el
   * backend caído no se siembra nada y no salen avisos falsos) y se hace UNA vez por ámbito; una
   * bandeja vacía también se siembra, porque a partir de ahí todo lo que llegue es nuevo.
   */
  useEffect(() => {
    if (!ambito || sembrado || !cargado) return;
    sembrar(ambito, entradasDeVistos(conversaciones));
  }, [ambito, sembrado, cargado, conversaciones, sembrar]);

  /**
   * La conversación ABIERTA se marca vista sola, en cada refresco: mientras se lee un chat sus
   * mensajes no cuentan como novedad (igual que en WhatsApp). La marca se escribe solo si el último
   * mensaje es más reciente que lo ya visto, así el refresco de 30 s no re-renderiza nada.
   */
  const abierta = opciones.conversacionAbierta ?? null;
  useEffect(() => {
    if (!ambito || !abierta) return;

    const fila = conversaciones.find((conversacion) => conversacion.id === abierta);
    if (!fila) return;

    const vistoEn = ultimoMensajeEn(fila);
    if (vistoEn) marcarVisto(ambito, abierta, vistoEn);
  }, [ambito, abierta, conversaciones, marcarVisto]);

  return {
    alertas,
    marcarTodasVistas: () => {
      if (ambito) marcarVarios(ambito, entradasDeVistos(conversaciones));
    },
  };
}
